import type { MessageArchivePlainSegment } from './crypto';
import { decryptDirectHistoryCache } from '../historySecrets/directCache';
import { decryptGroupHistoryCache } from '../historySecrets/groupCache';
import { getDirectMessagesCreatedInRange, type StoredMessage } from '../messages/storage';
import { getGroupChats, getGroupMessagesCreatedInRange, getGroupParticipants, type StoredGroupChat, type StoredGroupMessage } from '../groupChats/storage';
import { useAuthStore, type Identity } from '../store/authStore';
import { useVaultStore } from '../store/vaultStore';
import { utf8ToBytes } from '../utils/encoding';

export type MessageArchiveTextRecord = {
  source: 'direct' | 'group';
  messageId: string;
  chatId?: string;
  threadKey?: string;
  senderIdentityId: string | null;
  recipientIdentityId?: string | null;
  createdAt: string;
  updatedAt: string;
  editedAt?: string | null;
  deletedAt?: string | null;
  revision?: number | null;
  kind: 'text' | 'system' | 'file_event' | 'deleted' | 'unavailable';
  text?: string;
  metadata?: Record<string, unknown>;
};

export type MessageArchiveConversationSnapshot = NonNullable<MessageArchivePlainSegment['conversationSnapshots']>[string];

export type BuildMessageArchiveSegmentResult = {
  segment: MessageArchivePlainSegment;
  messageCount: number;
  byteSize: number;
  skippedCount: number;
};

function toIso(timestamp: number | null | undefined): string | null {
  if (!timestamp || !Number.isFinite(timestamp)) return null;
  return new Date(timestamp).toISOString();
}

function getDirectUpdatedAt(message: StoredMessage): number {
  return Number(message.contentUpdatedAt || message.editedAt || message.deletedAt || message.serverTimestamp || 0);
}

function getGroupUpdatedAt(message: StoredGroupMessage): number {
  return Number(message.contentUpdatedAt || message.editedAt || message.deletedAt || message.createdAt || 0);
}

function getDirectFileEventText(message: StoredMessage): string {
  const file = message.directFileEvent;
  if (!file) return '';
  const direction = file.transferDirection === 'sent' ? 'sent' : 'received';
  return `[file ${direction}] ${file.fileName}`;
}

function getContactDisplayName(identityId: string): string | null {
  const contact = useVaultStore.getState().findContactByIdentityId(identityId);
  return contact?.displayName?.trim() || null;
}

function fallbackIdentityTitle(identityId: string): string {
  return identityId ? `${identityId.slice(0, 10)}...` : 'Unknown';
}

function buildDirectConversationSnapshot(params: {
  threadKey: string;
  localIdentity: Identity;
  peerIdentityId: string;
  peerIdentityName?: string | null;
  capturedAt: string;
}): MessageArchiveConversationSnapshot {
  const localName = params.localIdentity.identityName?.trim() || params.localIdentity.serverName?.trim() || null;
  const peerName = getContactDisplayName(params.peerIdentityId) || params.peerIdentityName?.trim() || null;
  return {
    conversationId: `direct:${params.threadKey}`,
    type: 'direct',
    title: peerName || fallbackIdentityTitle(params.peerIdentityId),
    directThreadKey: params.threadKey,
    participants: [
      {
        identityId: params.localIdentity.identityId,
        displayName: localName,
        role: 'self'
      },
      {
        identityId: params.peerIdentityId,
        displayName: peerName,
        role: 'peer'
      }
    ],
    capturedAt: params.capturedAt
  };
}

async function buildGroupConversationSnapshot(params: {
  chat: StoredGroupChat;
  capturedAt: string;
}): Promise<MessageArchiveConversationSnapshot> {
  const participants = await getGroupParticipants(params.chat.chatId).catch(() => []);
  return {
    conversationId: `group:${params.chat.chatId}`,
    type: 'group',
    title: params.chat.title?.trim() || params.chat.chatId,
    groupChatId: params.chat.chatId,
    participants: participants.map((participant) => ({
      identityId: participant.identityId,
      displayName: participant.displayName,
      role: 'participant'
    })),
    capturedAt: params.capturedAt
  };
}

async function buildDirectRecord(params: {
  message: StoredMessage;
  identity: Identity;
}): Promise<MessageArchiveTextRecord | null> {
  const { message, identity } = params;
  if (message.senderIdentityId !== identity.identityId && message.recipientIdentityId !== identity.identityId) return null;

  const base: Omit<MessageArchiveTextRecord, 'kind'> = {
    source: 'direct',
    messageId: message.serverMessageId,
    threadKey: message.threadKey,
    senderIdentityId: message.senderIdentityId,
    recipientIdentityId: message.recipientIdentityId,
    createdAt: new Date(message.serverTimestamp).toISOString(),
    updatedAt: new Date(getDirectUpdatedAt(message)).toISOString(),
    editedAt: toIso(message.editedAt),
    deletedAt: toIso(message.deletedAt),
    revision: message.revision ?? null
  };

  if (message.deletedAt) {
    return { ...base, kind: 'deleted' };
  }
  if (message.kind === 'direct_file_event') {
    return {
      ...base,
      kind: 'file_event',
      text: getDirectFileEventText(message),
      metadata: message.directFileEvent ? { directFileEvent: message.directFileEvent } : undefined
    };
  }
  if (!message.localHistoryCiphertext) {
    return { ...base, kind: 'unavailable' };
  }

  const text = await decryptDirectHistoryCache({
    payload: message.localHistoryCiphertext,
    identityId: identity.identityId,
    serverId: identity.serverId || null,
    ecdhScalar: identity.ecdhScalar
  });
  if (text === null) return { ...base, kind: 'unavailable' };
  return { ...base, kind: 'text', text };
}

async function buildGroupRecord(params: {
  message: StoredGroupMessage;
  identity: Identity;
  allowedChatIds: Set<string>;
}): Promise<MessageArchiveTextRecord | null> {
  const { message, identity, allowedChatIds } = params;
  if (!allowedChatIds.has(message.chatId)) return null;

  const base: Omit<MessageArchiveTextRecord, 'kind'> = {
    source: 'group',
    messageId: message.messageId,
    chatId: message.chatId,
    senderIdentityId: message.senderIdentityId,
    createdAt: new Date(message.createdAt).toISOString(),
    updatedAt: new Date(getGroupUpdatedAt(message)).toISOString(),
    editedAt: toIso(message.editedAt),
    deletedAt: toIso(message.deletedAt),
    revision: message.revision ?? null
  };

  if (message.deletedAt) {
    return { ...base, kind: 'deleted' };
  }
  if (message.kind === 'system') {
    return {
      ...base,
      kind: 'system',
      metadata: {
        systemType: message.systemType,
        systemPayload: message.systemPayload
      }
    };
  }
  if (!message.localHistoryCiphertext) {
    return { ...base, kind: 'unavailable' };
  }

  const text = await decryptGroupHistoryCache({
    payload: message.localHistoryCiphertext,
    identityId: identity.identityId,
    serverId: identity.serverId || null,
    chatId: message.chatId,
    ecdhScalar: identity.ecdhScalar
  });
  if (text === null) return { ...base, kind: 'unavailable' };
  return { ...base, kind: 'text', text };
}

export async function buildMessageArchiveTextSegment(params: {
  sourceCircleIdentityId: string;
  periodKey: string;
  periodStartedAt: string | null;
  periodEndedAt: string;
  state?: 'current' | 'final';
}): Promise<BuildMessageArchiveSegmentResult> {
  const identity = useAuthStore.getState().identities[params.sourceCircleIdentityId];
  if (!identity) throw new Error('Archive source identity is not available on this device');

  const start = params.periodStartedAt ? Date.parse(params.periodStartedAt) : 0;
  const end = Date.parse(params.periodEndedAt);
  if (!Number.isFinite(start) || !Number.isFinite(end) || end <= start) {
    throw new Error('Invalid archive period range');
  }

  const [directMessages, groupChats, groupMessages] = await Promise.all([
    getDirectMessagesCreatedInRange(start, end),
    getGroupChats(),
    getGroupMessagesCreatedInRange(start, end)
  ]);
  const allowedChatIds = new Set(
    groupChats
      .filter((chat) => !chat.localIdentityId || chat.localIdentityId === identity.identityId)
      .map((chat) => chat.chatId)
  );

  const records: MessageArchiveTextRecord[] = [];
  const conversationSnapshots: Record<string, MessageArchiveConversationSnapshot> = {};
  let skippedCount = 0;
  const capturedAt = new Date().toISOString();
  const groupChatsById = new Map(groupChats.map((chat) => [chat.chatId, chat]));

  for (const message of directMessages) {
    const record = await buildDirectRecord({ message, identity });
    if (!record) continue;
    if (record.threadKey) {
      const peerIdentityId = record.senderIdentityId === identity.identityId
        ? record.recipientIdentityId
        : record.senderIdentityId;
      if (peerIdentityId) {
        const conversationId = `direct:${record.threadKey}`;
        conversationSnapshots[conversationId] = conversationSnapshots[conversationId] || buildDirectConversationSnapshot({
          threadKey: record.threadKey,
          localIdentity: identity,
          peerIdentityId,
          peerIdentityName: message.senderIdentityId === peerIdentityId ? message.senderIdentityName : null,
          capturedAt
        });
      }
    }
    if (record.kind === 'unavailable') skippedCount += 1;
    records.push(record);
  }

  for (const message of groupMessages) {
    const record = await buildGroupRecord({ message, identity, allowedChatIds });
    if (!record) continue;
    const conversationId = `group:${message.chatId}`;
    if (!conversationSnapshots[conversationId]) {
      const chat = groupChatsById.get(message.chatId);
      if (chat) {
        conversationSnapshots[conversationId] = await buildGroupConversationSnapshot({ chat, capturedAt });
      }
    }
    if (record.kind === 'unavailable') skippedCount += 1;
    records.push(record);
  }

  records.sort((a, b) => Date.parse(a.createdAt) - Date.parse(b.createdAt) || a.messageId.localeCompare(b.messageId));

  const segment: MessageArchivePlainSegment = {
    version: 1,
    periodKey: params.periodKey,
    periodStartedAt: params.periodStartedAt,
    periodEndedAt: params.periodEndedAt,
    state: params.state || 'current',
    createdAt: capturedAt,
    conversationSnapshots,
    messages: records as Array<Record<string, unknown>>
  };
  const byteSize = utf8ToBytes(JSON.stringify(segment)).byteLength;

  return {
    segment,
    messageCount: records.length,
    byteSize,
    skippedCount
  };
}
